import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { VehicleForm } from '../components/forms/VehicleForm';
import { InspectionForm } from '../components/forms/InspectionForm';
import { formatDisplayDate, getCurrentAESTDate } from '../utils/dateUtils';
import { Plus, Truck, Calendar, AlertTriangle, CheckCircle, Clock, CreditCard as Edit, Trash2, Eye, Search, Filter, FileText, Shield, Wrench, X } from 'lucide-react';

interface Vehicle {
  id: string;
  registration: string;
  type: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  rego_expiry_date: string | null;
  rego_na?: boolean;
  status: string;
  current_location?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface VehicleInspection {
  id: string;
  vehicle_id: string;
  inspection_type: string;
  inspection_date: string;
  expiry_date: string;
  inspector_name?: string;
  inspector_company?: string;
  certificate_number?: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface VehicleWithInspections extends Vehicle {
  inspections?: VehicleInspection[];
  rego_days_until_expiry?: number;
  rego_overdue?: boolean;
  nhvr_inspection?: VehicleInspection;
  nhvr_days_until_expiry?: number;
  nhvr_overdue?: boolean;
}

export const VehiclesPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<VehicleWithInspections[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleWithInspections | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      // Fetch vehicles
      const { data: vehiclesData, error: vehiclesError } = await supabase
        .from('vehicles')
        .select('*')
        .order('registration');

      if (vehiclesError) throw vehiclesError;

      // Fetch inspections
      const { data: inspectionsData, error: inspectionsError } = await supabase
        .from('vehicle_inspections')
        .select('*')
        .order('inspection_date', { ascending: false });

      if (inspectionsError) throw inspectionsError;

      // Combine vehicles with their inspections and calculate status
      const today = getCurrentAESTDate();
      
      const vehiclesWithInspections: VehicleWithInspections[] = (vehiclesData || []).map(vehicle => {
        const vehicleInspections = (inspectionsData || []).filter(inspection => inspection.vehicle_id === vehicle.id);

        // Calculate rego expiry status — skip for N/A vehicles
        let rego_days_until_expiry: number | undefined = undefined;
        let rego_overdue = false;
        if (!vehicle.rego_na && vehicle.rego_expiry_date) {
          const regoExpiryDate = new Date(vehicle.rego_expiry_date);
          const regoDiffTime = regoExpiryDate.getTime() - today.getTime();
          rego_days_until_expiry = Math.ceil(regoDiffTime / (1000 * 60 * 60 * 24));
          rego_overdue = rego_days_until_expiry < 0;
        }
        // Calculate NHVR status - only for vehicles that require NHVR
        let nhvr_inspection = undefined;
        let nhvr_days_until_expiry = 0;
        let nhvr_overdue = false;

        // Only calculate NHVR status if the vehicle requires NHVR
        if (vehicle.nhvr_required === true) {
          const nhvrInspections = (inspectionsData || []).filter(i =>
            i.vehicle_id === vehicle.id && i.inspection_type === 'nhvr'
          );
          nhvr_inspection = nhvrInspections.length > 0 ? nhvrInspections[0] : undefined;

          if (nhvr_inspection) {
            const nhvrExpiryDate = new Date(nhvr_inspection.expiry_date);
            const nhvrDiffTime = nhvrExpiryDate.getTime() - today.getTime();
            nhvr_days_until_expiry = Math.ceil(nhvrDiffTime / (1000 * 60 * 60 * 24));
            nhvr_overdue = nhvr_days_until_expiry < 0;
          } else {
            // No inspection record for a vehicle that requires NHVR = overdue
            nhvr_overdue = true;
          }
        }

        return {
          ...vehicle,
          inspections: vehicleInspections,
          rego_days_until_expiry,
          rego_overdue,
          nhvr_inspection,
          nhvr_days_until_expiry,
          nhvr_overdue
        };
      });

      setVehicles(vehiclesWithInspections);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVehicle = async (vehicleData: Partial<Vehicle>, nhvrInspectionData?: any) => {
    setSubmitting(true);
    try {
      let savedVehicle;
      if (editingVehicle) {
        const { error } = await supabase
          .from('vehicles')
          .update(vehicleData)
          .eq('id', editingVehicle.id);
        
        if (error) throw error;
        savedVehicle = editingVehicle;
      } else {
        const { data, error } = await supabase
          .from('vehicles')
          .insert([vehicleData])
          .select()
          .single();
        
        if (error) throw error;
        savedVehicle = data;
      }
      
      // If NHVR inspection data was provided, create the inspection record
      if (nhvrInspectionData && savedVehicle) {
        // Only create inspection record if we have an actual inspection date
        if (nhvrInspectionData.inspection_date) {
          // Calculate expiry date (12 months from inspection date)
          const inspectionDate = new Date(nhvrInspectionData.inspection_date);
          const expiryDate = new Date(inspectionDate);
          expiryDate.setFullYear(inspectionDate.getFullYear() + 1);
          
          // Create a note about the inspection location
          const inspectionNote = nhvrInspectionData.town ? `Inspection completed in ${nhvrInspectionData.town}` : 'NHVR inspection completed';
          
          const { error: inspectionError } = await supabase
            .from('vehicle_inspections')
            .insert([{
              vehicle_id: savedVehicle.id,
              inspection_type: 'nhvr',
              inspection_date: nhvrInspectionData.inspection_date,
              expiry_date: expiryDate.toISOString().split('T')[0],
              status: 'current',
              notes: inspectionNote
            }]);
          
          if (inspectionError) {
            console.error('Error creating NHVR inspection record:', inspectionError);
            alert('Vehicle saved but failed to create NHVR inspection record. You can add it manually later.');
          }
        }
      }
      
      setShowVehicleModal(false);
      setEditingVehicle(null);
      fetchVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      alert('Failed to save vehicle. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitInspection = async (inspectionData: Partial<VehicleInspection>) => {
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('vehicle_inspections')
        .insert([inspectionData]);
      
      if (error) throw error;
      
      setShowInspectionModal(false);
      setSelectedVehicle(null);
      fetchVehicles();
    } catch (error) {
      console.error('Error saving inspection:', error);
      alert('Failed to save inspection. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId: string) => {
    if (confirm('Are you sure you want to delete this vehicle? This will also delete all associated inspections.')) {
      try {
        const { error } = await supabase
          .from('vehicles')
          .delete()
          .eq('id', vehicleId);

        if (error) throw error;
        fetchVehicles();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        alert('Failed to delete vehicle. Please try again.');
      }
    }
  };


  const getRegoStatusColor = (vehicle: VehicleWithInspections) => {
    if (vehicle.rego_overdue) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    if (vehicle.rego_days_until_expiry !== undefined && vehicle.rego_days_until_expiry <= 30) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    if (vehicle.rego_days_until_expiry !== undefined && vehicle.rego_days_until_expiry <= 90) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getNHVRStatusColor = (vehicle: VehicleWithInspections) => {
    if (!vehicle.nhvr_inspection || vehicle.nhvr_overdue) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    if (vehicle.nhvr_days_until_expiry !== undefined && vehicle.nhvr_days_until_expiry <= 30) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    if (vehicle.nhvr_days_until_expiry !== undefined && vehicle.nhvr_days_until_expiry <= 90) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getStatusIcon = (isOverdue: boolean, daysUntilExpiry?: number) => {
    if (isOverdue) {
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    }
    
    if (daysUntilExpiry !== undefined && daysUntilExpiry <= 30) {
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    }
    
    if (daysUntilExpiry !== undefined && daysUntilExpiry <= 90) {
      return <Clock className="h-4 w-4 text-yellow-600" />;
    }
    
    return <CheckCircle className="h-4 w-4 text-green-600" />;
  };

  const getStatusText = (isOverdue: boolean, daysUntilExpiry?: number, hasRecord?: boolean) => {
    if (!hasRecord) {
      return 'No Record';
    }
    
    if (isOverdue) {
      return `Overdue (${Math.abs(daysUntilExpiry || 0)} days)`;
    }
    
    if (daysUntilExpiry !== undefined && daysUntilExpiry <= 30) {
      return `Due (${daysUntilExpiry} days)`;
    }
    
    if (daysUntilExpiry !== undefined && daysUntilExpiry <= 90) {
      return `Due in ${daysUntilExpiry} days`;
    }
    
    return `Current (${daysUntilExpiry} days)`;
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = vehicle.registration.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || vehicle.type === filterType;
    const matchesStatus = !filterStatus || vehicle.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const regoOverdueCount = vehicles.filter(v => !v.rego_na && v.rego_overdue).length;
  const nhvrOverdueCount = vehicles.filter(v => v.nhvr_required === true && (!v.nhvr_inspection || v.nhvr_overdue)).length;
  const regoDueCount = vehicles.filter(v => !v.rego_na && !v.rego_overdue && (v.rego_days_until_expiry ?? Infinity) <= 30 && (v.rego_days_until_expiry ?? Infinity) >= 0).length;
  const nhvrDueCount = vehicles.filter(v => v.nhvr_required === true && v.nhvr_inspection && !v.nhvr_overdue && (v.nhvr_days_until_expiry || 0) <= 30 && (v.nhvr_days_until_expiry || 0) >= 0).length;
  const regoDueSoonCount = regoDueCount;
  const nhvrDueSoonCount = nhvrDueCount;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle & Trailer Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Track vehicle registrations and NHVR inspection compliance
          </p>
        </div>
        <button
          onClick={() => {
            setEditingVehicle(null);
            setShowVehicleModal(true);
          }}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Vehicle</span>
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Truck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Vehicles</p>
              <p className="text-xl font-bold text-gray-900">{vehicles.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Rego Overdue</p>
              <p className="text-xl font-bold text-red-600">{regoOverdueCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Rego Due</p>
              <p className="text-xl font-bold text-red-600">{regoDueCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Shield className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">NHVR Overdue</p>
              <p className="text-xl font-bold text-red-600">{nhvrOverdueCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Shield className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">NHVR Due</p>
              <p className="text-xl font-bold text-red-600">{nhvrDueCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Due Soon (Combined)</p>
              <p className="text-xl font-bold text-yellow-600">{regoDueSoonCount + nhvrDueSoonCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert for overdue vehicles */}
      {(regoOverdueCount > 0 || nhvrOverdueCount > 0 || regoDueCount > 0 || nhvrDueCount > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-lg font-medium text-red-900">
                Vehicle Compliance Issues
              </h3>
              <p className="text-sm text-red-700">
                {regoOverdueCount} overdue registration • {regoDueCount} registration due • {nhvrOverdueCount} overdue NHVR • {nhvrDueCount} NHVR due
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by registration, make, or model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Types</option>
              <option value="truck">Truck</option>
              <option value="trailer">Trailer</option>
              <option value="ute">Ute</option>
              <option value="van">Van</option>
              <option value="car">Car</option>
              <option value="excavator">Excavator</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="maintenance">Maintenance</option>
              <option value="sold">Sold</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vehicle Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVehicles.map((vehicle) => (
          <div
            key={vehicle.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            {/* Vehicle Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Truck className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{vehicle.registration}</h3>
                  <p className="text-sm text-gray-600 capitalize">{vehicle.type}</p>
                  {vehicle.make && vehicle.model && (
                    <p className="text-xs text-gray-500 font-bold">{vehicle.make} {vehicle.model}</p>
                  )}
                </div>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => {
                    setSelectedVehicle(vehicle);
                    setShowDetailModal(true);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="View Details"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setEditingVehicle(vehicle);
                    setShowVehicleModal(true);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Edit Vehicle"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteVehicle(vehicle.id)}
                  className="p-1 text-gray-400 hover:text-red-600"
                  title="Delete Vehicle"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Registration Status */}
            {vehicle.rego_na ? (
              <div className="rounded-lg p-3 border mb-4 bg-gray-100 border-gray-200">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Registration N/A</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Vehicle off road — registration not tracked</p>
              </div>
            ) : (
              <div className={`rounded-lg p-3 border mb-4 ${getRegoStatusColor(vehicle)}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {vehicle.rego_overdue ? 'Rego Overdue' :
                       (vehicle.rego_days_until_expiry !== undefined && vehicle.rego_days_until_expiry <= 30) ? 'Rego Due' :
                       'Registration'}
                    </span>
                  </div>
                  {getStatusIcon(vehicle.rego_overdue || false, vehicle.rego_days_until_expiry)}
                </div>
                <div className="space-y-1">
                  <div>
                    <p className="text-xs font-medium">Expires:</p>
                    <p className="text-sm font-bold">
                      {vehicle.rego_expiry_date ? formatDisplayDate(vehicle.rego_expiry_date) : '—'}
                    </p>
                  </div>
                  <div className="pt-1">
                    <span className="text-xs font-medium">
                      {getStatusText(vehicle.rego_overdue || false, vehicle.rego_days_until_expiry, true)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* NHVR Inspection Status - Only show if NHVR is required */}
            {vehicle.nhvr_required && (
              <div className={`rounded-lg p-3 border mb-4 ${getNHVRStatusColor(vehicle)}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {(!vehicle.nhvr_inspection || vehicle.nhvr_overdue) ? 'NHVR Overdue' :
                       (vehicle.nhvr_days_until_expiry !== undefined && vehicle.nhvr_days_until_expiry <= 30) ? 'NHVR Due' :
                       'NHVR Inspection'}
                    </span>
                  </div>
                  {getStatusIcon(vehicle.nhvr_overdue || false, vehicle.nhvr_days_until_expiry)}
                </div>
                
                <div className="space-y-1">
                  <div>
                    <p className="text-xs font-medium">Last Inspection:</p>
                    <p className="text-sm font-bold">
                      {vehicle.nhvr_inspection?.inspection_date 
                        ? formatDisplayDate(vehicle.nhvr_inspection.inspection_date)
                        : 'No Record'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium">Next Due:</p>
                    <p className="text-sm font-bold">
                      {vehicle.nhvr_inspection?.expiry_date 
                        ? formatDisplayDate(vehicle.nhvr_inspection.expiry_date)
                        : 'Immediate'
                      }
                    </p>
                  </div>
                  
                  {/* Scheduled NHVR Information */}
                  {(vehicle.nhvr_scheduled_date || vehicle.nhvr_town) && (
                    <div className="pt-2 border-t border-gray-300">
                      {vehicle.nhvr_scheduled_date && (
                        <div>
                          <p className="text-xs font-medium">Scheduled:</p>
                          <p className="text-sm font-bold text-blue-700">
                            {formatDisplayDate(vehicle.nhvr_scheduled_date)}
                          </p>
                        </div>
                      )}
                      {vehicle.nhvr_town && (
                        <div>
                          <p className="text-xs font-medium">Location:</p>
                          <p className="text-sm font-bold text-blue-700">
                            {vehicle.nhvr_town}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="pt-1">
                    <span className="text-xs font-medium">
                      {getStatusText(
                        vehicle.nhvr_overdue || false, 
                        vehicle.nhvr_days_until_expiry,
                        !!vehicle.nhvr_inspection
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* NHVR Not Required Message */}
            {!vehicle.nhvr_required && (
              <div className="rounded-lg p-3 border mb-4 bg-gray-100 border-gray-200">
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">NHVR Not Required</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  This vehicle does not require NHVR inspections
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setSelectedVehicle(vehicle);
                  setShowInspectionModal(true);
                }}
                className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
              >
                <Plus className="h-4 w-4" />
                <span>Add Inspection</span>
              </button>
              <button
                onClick={() => {
                  setSelectedVehicle(vehicle);
                  setShowDetailModal(true);
                }}
                className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                title="View Details"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredVehicles.length === 0 && (
        <div className="text-center py-12">
          <Truck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterType || filterStatus ? 'No matching vehicles found' : 'No vehicles registered'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterType || filterStatus 
              ? 'Try adjusting your search or filter criteria.'
              : 'Add your first vehicle to start tracking registrations and inspections.'
            }
          </p>
          {(searchTerm || filterType || filterStatus) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterType('');
                setFilterStatus('');
              }}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Vehicle Form Modal */}
      {showVehicleModal && (
        <VehicleForm
          vehicle={editingVehicle}
          onSubmit={handleSubmitVehicle}
          onClose={() => {
            setShowVehicleModal(false);
            setEditingVehicle(null);
          }}
          loading={submitting}
        />
      )}

      {/* Inspection Form Modal */}
      {showInspectionModal && selectedVehicle && (
        <InspectionForm
          vehicle={selectedVehicle}
          onSubmit={handleSubmitInspection}
          onClose={() => {
            setShowInspectionModal(false);
            setSelectedVehicle(null);
          }}
          loading={submitting}
        />
      )}

      {/* Vehicle Detail Modal */}
      {showDetailModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Truck className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Vehicle Details</h3>
                  <p className="text-sm text-gray-500">{selectedVehicle.registration}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedVehicle(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Vehicle Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Vehicle Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration</label>
                    <p className="text-sm text-gray-900 font-mono">{selectedVehicle.registration}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <p className="text-sm text-gray-900 capitalize">{selectedVehicle.type}</p>
                  </div>
                  {selectedVehicle.make && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                      <p className="text-sm text-gray-900">{selectedVehicle.make}</p>
                    </div>
                  )}
                  {selectedVehicle.model && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      <p className="text-sm text-gray-900">{selectedVehicle.model}</p>
                    </div>
                  )}
                  {selectedVehicle.year && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                      <p className="text-sm text-gray-900">{selectedVehicle.year}</p>
                    </div>
                  )}
                  {selectedVehicle.vin && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                      <p className="text-sm text-gray-900 font-mono">{selectedVehicle.vin}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Expiry</label>
                    <p className="text-sm text-gray-900">{formatDisplayDate(selectedVehicle.rego_expiry_date)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedVehicle.status === 'active' ? 'bg-green-100 text-green-800' :
                      selectedVehicle.status === 'inactive' ? 'bg-red-100 text-red-800' :
                      selectedVehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedVehicle.status.charAt(0).toUpperCase() + selectedVehicle.status.slice(1)}
                    </span>
                  </div>
                </div>
                
                {selectedVehicle.notes && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <p className="text-sm text-gray-900 bg-white rounded-md p-3 border">{selectedVehicle.notes}</p>
                  </div>
                )}
              </div>

              {/* Inspection History */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Inspection History</h4>
                  <button
                    onClick={() => {
                      setShowInspectionModal(true);
                    }}
                    className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Inspection</span>
                  </button>
                </div>
                
                {selectedVehicle.inspections && selectedVehicle.inspections.length > 0 ? (
                  <div className="space-y-3">
                    {selectedVehicle.inspections.map((inspection) => (
                      <div key={inspection.id} className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Shield className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900 capitalize">
                              {inspection.inspection_type.replace('_', ' ')} Inspection
                            </span>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            inspection.status === 'current' ? 'bg-green-100 text-green-800' :
                            inspection.status === 'due_soon' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {inspection.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Inspection Date:</span>
                            <p className="text-gray-900">{formatDisplayDate(inspection.inspection_date)}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Expiry Date:</span>
                            <p className="text-gray-900">{formatDisplayDate(inspection.expiry_date)}</p>
                          </div>
                          {inspection.inspector_name && (
                            <div>
                              <span className="font-medium text-gray-700">Inspector:</span>
                              <p className="text-gray-900">{inspection.inspector_name}</p>
                            </div>
                          )}
                          {inspection.certificate_number && (
                            <div>
                              <span className="font-medium text-gray-700">Certificate:</span>
                              <p className="text-gray-900 font-mono">{inspection.certificate_number}</p>
                            </div>
                          )}
                        </div>
                        
                        {inspection.notes && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <span className="font-medium text-gray-700 text-sm">Notes:</span>
                            <p className="text-sm text-gray-900 mt-1">{inspection.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Shield className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No inspections recorded for this vehicle</p>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Compliance Summary */}
      {vehicles.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Fleet Compliance Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {Math.round(((vehicles.length - regoOverdueCount) / vehicles.length) * 100)}%
              </div>
              <div className="text-sm text-gray-600">Registration Compliance</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {vehicles.filter(v => v.nhvr_required).length > 0 ? 
                  Math.round(((vehicles.filter(v => v.nhvr_required).length - nhvrOverdueCount) / vehicles.filter(v => v.nhvr_required).length) * 100) : 100}%
              </div>
              <div className="text-sm text-gray-600">NHVR Compliance</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {vehicles.filter(v => v.status === 'active').length}
              </div>
              <div className="text-sm text-gray-600">Active Vehicles</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {vehicles.filter(v => v.nhvr_required === true && v.inspections && v.inspections.length > 0).length}
              </div>
              <div className="text-sm text-gray-600">NHVR Vehicles with Records</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};